<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative z-6000"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
                        >
                            <div class="absolute top-4 right-3 hidden pt-4 pr-4 sm:block">
                                <button
                                    type="button"
                                    class="cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="flex items-start">
                                <div
                                    class="mx-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0"
                                >
                                    <MdiLink
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div class="pt-1 text-center sm:ml-2 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        class="text-base leading-6 font-semibold text-gray-900"
                                    >
                                        {{ state.title }}
                                    </DialogTitle>
                                </div>
                            </div>

                            <div class="block px-2 py-2">
                                <label
                                    for="name"
                                    class="block text-sm font-medium text-gray-700 select-none"
                                >
                                    {{ t('editorInsertProtocolAndAddress') }}
                                </label>

                                <div class="mt-1 flex">
                                    <select
                                        id="placeholders"
                                        v-model="defaultProtocol.name"
                                        name="placeholders"
                                        class="rounded-l-md border border-r-0 border-gray-300 bg-white font-medium shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                    >
                                        <option
                                            v-for="protocol in allowedProtocols"
                                            :key="protocol"
                                            class="font-medium"
                                            :value="protocol"
                                        >
                                            {{ protocol }}
                                        </option>
                                    </select>
                                    <input
                                        id="name"
                                        v-model="linkAddress"
                                        type="text"
                                        name="name"
                                        autocomplete="off"
                                        class="block w-full grow rounded-r-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                        @keyup.enter="
                                            if (defaultProtocol.detected) {
                                                insertEditorContent();
                                            }
                                        "
                                    />
                                </div>
                                <AlertBox
                                    v-if="!defaultProtocol.detected"
                                    state="danger"
                                    :icon="MdiAlert"
                                    :title="t('editorLinkInvalid')"
                                    class-override=" -m-px rounded-t-none -mt-2"
                                >
                                </AlertBox>
                                <AlertBox
                                    v-if="defaultProtocol.detected"
                                    state="success"
                                    :icon="MdiInformation"
                                    :title="t('editorLinkValid')"
                                    class-override=" -m-px rounded-t-none -mt-2"
                                >
                                </AlertBox>
                            </div>
                            <div class="block px-2 py-2 text-right">
                                <ActionButton
                                    :value="t('buttonInsert')"
                                    state="primary"
                                    :icon="MdiLinkPlus"
                                    :is-disabled="!defaultProtocol.detected"
                                    @click="insertEditorContent()"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { reactive, ref, watch } from 'vue';
import AlertBox from '../ui/AlertBox.vue';
import ActionButton from '../ui/ActionButton.vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
    MdiInformation,
    MdiAlert,
    MdiLink,
    MdiLinkPlus,
    MdiClose,
} from 'materialdesignicons-vue3/icons/';
import { t } from '../composables/i18n';

defineExpose({
    name: 'LinkInsert',
});

const props = defineProps({
    state: {
        type: Object,
        required: true,
    },
});

const state = props.state;

const allowedProtocols = ['https://', 'http://', 'mailto:', 'tel:', 'placeholder:'];

function getCurrentHref(editor) {
    let href = '';

    if (!editor) {
        console.log('Editor is not initialized.');
        return href;
    }

    const { state, commands } = editor;
    const { selection, doc } = state;
    // Hledáme od pozice $from a $to v dokumentu
    const $from = selection.$from;
    const $to = selection.empty ? $from : selection.$to;

    if (!selection.empty) {
        const { from, to } = selection;
        const editorText = state.doc.textBetween(from, to, '\n').trim();
        if (editorText.length > 0) {
            href = editorText;
        }
    }
    // Projdeme všechny uzly od $from po $to
    doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
        // Hledáme textový uzel s mark typu 'link'
        if (node.isInline && node.marks.length) {
            const linkMark = node.marks.find((mark) => mark.type.name === 'link');
            if (linkMark) {
                href = linkMark.attrs.href;
                const startPos = pos;
                const endPos = pos + node.nodeSize;
                if (href) {
                    commands.setTextSelection({ from: startPos, to: endPos });
                    return false; // Ukončíme procházení uzlů, protože jsme našli, co jsme hledali
                }
            }
        }
        return true; // Pokračujeme v procházení dalších uzlů
    });

    return href;
}

const detectProtocol = (link) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // alternative: const httpLinkRegex = /^http:\/\/([\da-z.-]+)\.([a-z.]{1,10})([/\w .-]*)*\/?$/;
    // alternative: const httpsLinkRegex = /^https:\/\/([\da-z.-]+)\.([a-z.]{1,10})([/\w .-]*)*\/?$/;
    const httpLinkRegex = /^http:\/\/([\da-z.-]+)\.([a-z.]{2,20})([/\w .-]*)(\?.*)?\/?$/;
    const httpsLinkRegex = /^https:\/\/([\da-z.-]+)\.([a-z.]{2,20})([/\w .-]*)(\?.*)?\/?$/;
    const phoneNumberRegex = /^\+?(\d[\d\-.\s]+)?(\([\d\-.\s]+\))?[\d\-.\s]+$/;
    const placeholderRegex =
        /^\[=(item\.[a-zA-Z_]\w*|([a-zA-Z_][\w]*)(?:\[\d+\])\.([a-zA-Z_]\w*))\s*\]$/;
    const protocol = {
        name: 'unknown',
        default: 'https://',
        detected: false,
        input: link,
    };

    if (emailRegex.test(link)) {
        protocol.name = 'mailto:';
        protocol.detected = true;
    } else if (httpsLinkRegex.test(link)) {
        protocol.name = 'https://';
        protocol.detected = true;
    } else if (httpLinkRegex.test(link)) {
        protocol.name = 'http://';
        protocol.detected = true;
    } else if (phoneNumberRegex.test(link)) {
        protocol.name = 'tel:';
        protocol.detected = true;
    } else if (placeholderRegex.test(link)) {
        protocol.name = 'placeholder:';
        protocol.detected = true;
    }
    return protocol;
};

const editorFocus = (editor, left) => {
    setTimeout(function () {
        const pos = parseInt(editor.state.selection.$anchor.pos);
        editor.commands.focus(pos - left);
        editor.commands.scrollIntoView();
    }, 400);
};

const insertEditorContent = () => {
    const linkHref =
        defaultProtocol.name === 'placeholder:'
            ? linkAddress.value
            : `${defaultProtocol.name}${linkAddress.value}`;
    state.data.editor
        .chain()
        .focus()
        .setLink({
            href: linkHref,
            target: '_blank',
        })
        .run();
    editorFocus(state.data.editor, 0);
    state.open = false;
};

const linkAddress = ref('');
const canWatchLinkAddress = ref(false);
const defaultProtocol = reactive(detectProtocol(linkAddress.value));

const makeChanges = () => {
    const httpsFullProtocol = detectProtocol(`https://${linkAddress.value}`);
    defaultProtocol.detected = httpsFullProtocol.detected;
    defaultProtocol.default = allowedProtocols[0];
    if (httpsFullProtocol.detected) {
        defaultProtocol.default = httpsFullProtocol.default;
    }
    const fullProtocol = detectProtocol(linkAddress.value);
    if (fullProtocol.detected) {
        defaultProtocol.detected = fullProtocol.detected;
        defaultProtocol.default = fullProtocol.default;
    }
    defaultProtocol.name = fullProtocol.detected ? fullProtocol.name : defaultProtocol.default;
    linkAddress.value = linkAddress.value.replace(defaultProtocol.name, '');
};

watch(linkAddress, () => {
    if (state.open === true && canWatchLinkAddress.value === true) {
        makeChanges();
    }
});

watch(state, () => {
    if (state.open === true) {
        linkAddress.value = getCurrentHref(state.data.editor);
        makeChanges();
        canWatchLinkAddress.value = true;
    }
});

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
